import type { PageHelp } from 'help/types';

export const insurerReportsHelp: PageHelp = {
  title: 'Insurer reports',
  intro:
    'Import the Excel reports in which insurers list the premium and commission per policy, and check each entry against the installment (payment plan entry) or payment that PCT holds for it. Matching happens automatically on import; this page is where the leftovers are matched by hand and the differences are reviewed.',
  sections: [
    'Getting around',
    'Finding a report',
    'Reading the report list',
    'Reading the entries',
    'Import an insurer report',
    'Match an entry by hand',
    'Re-match a report',
    'Delete a report',
    'View the matched payment or installment',
  ],
  contexts: {
    'add-menu': 'Click "Add insurer report" in the second toolbar row.',
    'import-dialog': 'Click "Add insurer report", then "Import insurer report".',
    'match-entry-dialog':
      'Select a report, then click the link icon (or the re-match icon) in the last column of an entry.',
    'rematch-dialog': 'Click the re-match icon in the last column of a report row.',
    'delete-dialog': 'Click the delete icon in the last column of a report row.',
    'info-dialog': 'Select a report, then click the info icon in the last column of a matched entry.',
  },
  tips: [
    'An insurer report is the insurer side of premium collection: every entry is a policy for which the insurer reports premium and commission. Matching links the entry to the PCT installment or payment, so that differences show up as red cells and as unmatched counts.',
    'Automatic matching only considers payment plan entries of the same insurer and GrECo company whose policy number or policy sub-number equals the policy number in the report; of several candidates the one with the closest premium wins. For broker premium collection entries a cleared payment of that policy is preferred, and a payment can be linked to one entry only.',
    'Colours: orange entry rows were matched automatically, red cells differ from the PCT data, and a red "Unmatched entries" count means the report still has entries without a link.',
    'The insurer report number, date, premium, commission and payment date of a matched entry are also shown with its installment on the Payment plans page.',
    'Importing, matching, re-matching and deleting need write access in PCT; read-only users can browse the reports, but the server refuses these changes.',
  ],
  steps: [
    // Getting around
    {
      selector: '[data-help="insurer-reports-page-switcher"]',
      title: 'Switch to another PCT page',
      body: 'The toolbar moves between the PCT pages: bank statements, insurer reports, client balance, payment plans, reminders and payments. Go to Payment plans or Payments when an entry cannot be matched because the installment or payment is missing or wrong there.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Pereiti į kitą PCT puslapį',
          body: 'Įrankių juosta perjungia PCT puslapius: banko išrašus, draudiko ataskaitas, kliento balansą, mokėjimo planus, priminimus ir mokėjimus. Eikite į mokėjimo planus ar mokėjimus, kai įrašo nepavyksta susieti, nes ten trūksta įmokos ar mokėjimo arba jie neteisingi.',
        },
        lv: {
          title: 'Pāriet uz citu PCT lapu',
          body: 'Rīkjosla pārslēdz PCT lapas: bankas izrakstus, apdrošinātāja atskaites, klienta bilanci, maksājumu plānus, atgādinājumus un maksājumus. Dodieties uz maksājumu plāniem vai maksājumiem, ja ierakstu nevar sasaistīt, jo tur trūkst iemaksas vai maksājuma vai tie ir nepareizi.',
        },
      },
    },
    {
      selector: '[data-help="app-user-manual"]',
      title: 'Download the user manual',
      body: 'Downloads the complete PCT user manual as a PDF, with screenshots of every page. You get it in the language selected in Settings: Lithuanian or Latvian when the app is in that language, English otherwise. The page guide offers the same download (the book next to EN / LT / LV) in the guide’s language.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Atsisiųsti naudotojo vadovą',
          body: 'Atsisiunčia visą PCT naudotojo vadovą PDF formatu su visų puslapių ekrano kopijomis. Gaunate jį kalba, pasirinkta „Nustatymuose“: lietuvių arba latvių, kai programa veikia ta kalba, kitu atveju – anglų. Puslapio vadove tas pats atsisiuntimas (knygos piktograma šalia EN / LT / LV) pateikiamas vadovo kalba.',
        },
        lv: {
          title: 'Lejupielādēt lietotāja rokasgrāmatu',
          body: 'Lejupielādē pilnu PCT lietotāja rokasgrāmatu PDF formātā ar visu lapu ekrānuzņēmumiem. Jūs to saņemat valodā, kas izvēlēta sadaļā “Settings”: latviešu vai lietuviešu, ja lietotne darbojas šajā valodā, citādi – angļu. Lapas ceļvedī tā pati lejupielāde (grāmatas ikona blakus EN / LT / LV) ir pieejama ceļveža valodā.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-refresh"]',
      title: 'Reload the report list',
      body: 'Runs the query again with the current dates, insurer and companies. A report you have selected stays selected if it is still in the result, and its entries are reloaded too.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Atnaujinti ataskaitų sąrašą',
          body: 'Iš naujo vykdo užklausą su dabartinėmis datomis, draudiku ir įmonėmis. Pasirinkta ataskaita lieka pasirinkta, jei ji vis dar yra rezultatuose, ir jos įrašai taip pat įkeliami iš naujo.',
        },
        lv: {
          title: 'Pārlādēt atskaišu sarakstu',
          body: 'Atkārtoti izpilda vaicājumu ar pašreizējiem datumiem, apdrošinātāju un uzņēmumiem. Izvēlētā atskaite paliek izvēlēta, ja tā joprojām ir rezultātā, un arī tās ieraksti tiek pārlādēti.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-add-menu"]',
      title: 'Open the add menu',
      body: 'Opens a menu with "Import insurer report". Insurer reports are never typed in by hand; they always come from the Excel file the insurer sends.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Atidaryti pridėjimo meniu',
          body: 'Atidaro meniu su punktu „Importuoti draudiko ataskaitą“. Draudiko ataskaitos niekada nevedamos ranka – jos visada importuojamos iš draudiko atsiųsto Excel failo.',
        },
        lv: {
          title: 'Atvērt pievienošanas izvēlni',
          body: 'Atver izvēlni ar punktu “Importēt apdrošinātāja atskaiti”. Apdrošinātāja atskaites nekad netiek ievadītas manuāli – tās vienmēr nāk no apdrošinātāja atsūtītā Excel faila.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-import-menu-item"]',
      title: 'Start an import',
      body: 'Opens the "Import insurer report" dialog, where you choose the insurer, GrECo company, currency, report month and the Excel file. Nothing is saved until you click "Import" in the dialog.',
      section: 'Getting around',
      context: 'add-menu',
      i18n: {
        lt: {
          title: 'Pradėti importą',
          body: 'Atidaro langą „Importuoti draudiko ataskaitą“, kuriame pasirenkate draudiką, GrECo įmonę, valiutą, ataskaitos mėnesį ir Excel failą. Niekas neišsaugoma, kol lange nespustelite „Importuoti“.',
        },
        lv: {
          title: 'Sākt importu',
          body: 'Atver logu “Importēt apdrošinātāja atskaiti”, kurā izvēlaties apdrošinātāju, GrECo uzņēmumu, valūtu, atskaites mēnesi un Excel failu. Nekas netiek saglabāts, kamēr logā nenoklikšķināt “Import”.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-export-excel"]',
      title: 'Export the visible entries to Excel',
      body: 'Downloads the entries of the selected report exactly as the lower grid shows them, after the entry search and the perfect-match checkbox, with the report number as the first column. Cells that are red in the grid are marked in the workbook as well. The button stays disabled until at least one entry is visible, so select a report first.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Eksportuoti matomus įrašus į Excel',
          body: 'Atsisiunčia pasirinktos ataskaitos įrašus tiksliai taip, kaip juos rodo apatinė lentelė, pritaikius įrašų paiešką ir tobulų atitikmenų žymimąjį langelį; pirmame stulpelyje – ataskaitos numeris. Lentelėje raudoni langeliai pažymimi ir faile. Mygtukas neaktyvus, kol nematomas nė vienas įrašas, todėl pirmiausia pasirinkite ataskaitą.',
        },
        lv: {
          title: 'Eksportēt redzamos ierakstus uz Excel',
          body: 'Lejupielādē izvēlētās atskaites ierakstus tieši tā, kā tos rāda apakšējā tabula pēc ierakstu meklēšanas un ideālo atbilstību izvēles rūtiņas; pirmajā kolonnā ir atskaites numurs. Tabulā sarkanās šūnas tiek atzīmētas arī failā. Poga nav aktīva, kamēr nav redzams neviens ieraksts, tāpēc vispirms izvēlieties atskaiti.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-results-count"]',
      title: 'See how many reports are listed',
      body: 'Counts the reports currently visible in the upper grid, after the search box has been applied. If it shows fewer than you expect, clear the search or widen the date range.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Matyti, kiek ataskaitų rodoma',
          body: 'Skaičiuoja viršutinėje lentelėje šiuo metu matomas ataskaitas, pritaikius paieškos laukelį. Jei skaičius mažesnis nei tikėtasi, išvalykite paiešką arba išplėskite datų intervalą.',
        },
        lv: {
          title: 'Redzēt, cik atskaišu ir sarakstā',
          body: 'Skaita augšējā tabulā pašlaik redzamās atskaites pēc meklēšanas lauka piemērošanas. Ja skaits ir mazāks, nekā gaidīts, notīriet meklēšanu vai paplašiniet datumu intervālu.',
        },
      },
    },

    // Finding a report
    {
      selector: '[data-help="insurer-reports-search"]',
      title: 'Search the loaded reports',
      body: 'Filters the reports that are already loaded by report number, insurer, GrECo company or currency as you type. It does not ask the server again, so if a report is missing widen the dates instead. A selected report that no longer matches is deselected and its entries are cleared.',
      section: 'Finding a report',
      i18n: {
        lt: {
          title: 'Ieškoti tarp įkeltų ataskaitų',
          body: 'Rašant filtruoja jau įkeltas ataskaitas pagal ataskaitos numerį, draudiką, GrECo įmonę arba valiutą. Serveris iš naujo neužklausiamas, todėl jei ataskaitos trūksta, verčiau išplėskite datas. Pasirinkta ataskaita, kuri nebeatitinka paieškos, atžymima, o jos įrašai išvalomi.',
        },
        lv: {
          title: 'Meklēt ielādētajās atskaitēs',
          body: 'Rakstot filtrē jau ielādētās atskaites pēc atskaites numura, apdrošinātāja, GrECo uzņēmuma vai valūtas. Serveris netiek vaicāts atkārtoti, tāpēc, ja atskaites trūkst, labāk paplašiniet datumus. Izvēlētā atskaite, kas vairs neatbilst meklēšanai, tiek noņemta no izvēles, un tās ieraksti tiek notīrīti.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-from-date"]',
      title: 'Set the start of the date range',
      body: 'Reports dated on or after this day are loaded; the page opens with a date three months back. Pick a day or type it as day.month.year. Changing it reloads the list from the server.',
      section: 'Finding a report',
      i18n: {
        lt: {
          title: 'Nustatyti datų intervalo pradžią',
          body: 'Įkeliamos ataskaitos, kurių data yra ši diena arba vėlesnė; puslapis atidaromas su data prieš tris mėnesius. Pasirinkite dieną arba įveskite ją formatu diena.mėnuo.metai. Pakeitus datą sąrašas iš naujo įkeliamas iš serverio.',
        },
        lv: {
          title: 'Iestatīt datumu intervāla sākumu',
          body: 'Tiek ielādētas atskaites, kuru datums ir šī diena vai vēlāks; lapa atveras ar datumu pirms trim mēnešiem. Izvēlieties dienu vai ievadiet to formātā diena.mēnesis.gads. Mainot datumu, saraksts tiek pārlādēts no servera.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-to-date"]',
      title: 'Set the end of the date range',
      body: 'Reports dated up to and including this day are loaded; it starts as today. Leave both dates wide when you look for an older report, then narrow the list with the search box.',
      section: 'Finding a report',
      i18n: {
        lt: {
          title: 'Nustatyti datų intervalo pabaigą',
          body: 'Įkeliamos ataskaitos, kurių data yra iki šios dienos imtinai; pradžioje tai šiandiena. Ieškodami senesnės ataskaitos nustatykite platų intervalą, o tada susiaurinkite sąrašą paieškos laukeliu.',
        },
        lv: {
          title: 'Iestatīt datumu intervāla beigas',
          body: 'Tiek ielādētas atskaites, kuru datums ir līdz šai dienai ieskaitot; sākumā tā ir šodiena. Meklējot vecāku atskaiti, iestatiet plašu intervālu un pēc tam sašauriniet sarakstu ar meklēšanas lauku.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-date-field"]',
      title: 'Choose which date the range filters on',
      body: '"Insurer report date" is the month-end date chosen when the report was imported; "Insurer report created at" is the day it was imported into PCT. Use the second one to find what a colleague imported recently, whatever month it covers.',
      section: 'Finding a report',
      i18n: {
        lt: {
          title: 'Pasirinkti, pagal kurią datą filtruoti',
          body: '„Draudiko ataskaitos data“ – tai mėnesio paskutinė diena, pasirinkta importuojant ataskaitą; „Draudiko ataskaita sukurta“ – diena, kai ji buvo importuota į PCT. Antrąją naudokite norėdami rasti, ką kolega neseniai importavo, nepriklausomai nuo ataskaitos mėnesio.',
        },
        lv: {
          title: 'Izvēlēties, pēc kura datuma filtrēt',
          body: '“Apdrošinātāja atskaites datums” ir mēneša pēdējā diena, kas izvēlēta, importējot atskaiti; “Apdrošinātāja atskaite izveidota” ir diena, kad tā tika importēta PCT. Izmantojiet otro, lai atrastu, ko kolēģis nesen importējis, neatkarīgi no atskaites mēneša.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-insurer-filter"]',
      title: 'Filter by insurer',
      body: 'Type part of the insurer name and pick it from the list; the page reloads with only the reports of that insurer. Clear the field with the cross to see all insurers again.',
      section: 'Finding a report',
      i18n: {
        lt: {
          title: 'Filtruoti pagal draudiką',
          body: 'Įveskite dalį draudiko pavadinimo ir pasirinkite jį iš sąrašo – puslapis įkeliamas iš naujo tik su to draudiko ataskaitomis. Norėdami vėl matyti visus draudikus, išvalykite lauką kryželiu.',
        },
        lv: {
          title: 'Filtrēt pēc apdrošinātāja',
          body: 'Ievadiet daļu apdrošinātāja nosaukuma un izvēlieties to no saraksta – lapa tiek pārlādēta tikai ar šī apdrošinātāja atskaitēm. Lai atkal redzētu visus apdrošinātājus, notīriet lauku ar krustiņu.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-companies"]',
      title: 'Choose the GrECo companies',
      body: 'Only reports of the selected GrECo companies are loaded, and the server never returns companies you have no access to. The same company choice applies on the other PCT pages.',
      section: 'Finding a report',
      i18n: {
        lt: {
          title: 'Pasirinkti GrECo įmones',
          body: 'Įkeliamos tik pasirinktų GrECo įmonių ataskaitos, o įmonių, prie kurių neturite prieigos, serveris niekada negrąžina. Tas pats įmonių pasirinkimas galioja ir kituose PCT puslapiuose.',
        },
        lv: {
          title: 'Izvēlēties GrECo uzņēmumus',
          body: 'Tiek ielādētas tikai izvēlēto GrECo uzņēmumu atskaites, un uzņēmumus, kuriem jums nav piekļuves, serveris nekad neatgriež. Tā pati uzņēmumu izvēle attiecas arī uz citām PCT lapām.',
        },
      },
    },

    // Reading the report list
    {
      selector: '[data-help="insurer-reports-grid"]',
      title: 'Select a report and read its totals',
      body: 'Tick the checkbox at the start of a row to select a report; only one can be selected and its entries open in the lower grid. The sums show premium, commission and report net; "Insurer report payment date" and "amount" come from the outgoing bank statement entry that paid the report to the insurer with the report number as payment reference, and stay empty until that payment is imported. "Unmatched entries" turns red while any entry has no link.',
      section: 'Reading the report list',
      i18n: {
        lt: {
          title: 'Pasirinkti ataskaitą ir peržiūrėti jos sumas',
          body: 'Pažymėkite langelį eilutės pradžioje, kad pasirinktumėte ataskaitą; galima pasirinkti tik vieną, o jos įrašai atsidaro apatinėje lentelėje. Sumos rodo įmokas, komisinius ir ataskaitos neto; „Draudiko ataskaitos mokėjimo data“ ir „suma“ imamos iš išeinančio banko išrašo įrašo, kuriuo ataskaita apmokėta draudikui su ataskaitos numeriu mokėjimo paskirtyje, ir lieka tuščios, kol toks mokėjimas neimportuotas. „Nesutapę įrašai“ rodomi raudonai, kol bent vienas įrašas nesusietas.',
        },
        lv: {
          title: 'Izvēlēties atskaiti un apskatīt tās summas',
          body: 'Atzīmējiet rūtiņu rindas sākumā, lai izvēlētos atskaiti; var izvēlēties tikai vienu, un tās ieraksti atveras apakšējā tabulā. Summas rāda prēmiju, komisiju un atskaites neto; “Apdrošinātāja atskaites maksājuma datums” un “summa” tiek ņemti no izejošā bankas izraksta ieraksta, ar kuru atskaite apmaksāta apdrošinātājam ar atskaites numuru maksājuma mērķī, un paliek tukši, kamēr šis maksājums nav importēts. “Nesaskaņotie ieraksti” ir sarkani, kamēr kāds ieraksts nav sasaistīts.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-row-rematch"]',
      title: 'Re-match a whole report',
      body: 'Opens a confirmation to run the automatic matching again for every entry of this report. Use it after missing installments or payments have been added in PCT.',
      section: 'Reading the report list',
      optional: true,
      i18n: {
        lt: {
          title: 'Iš naujo susieti visą ataskaitą',
          body: 'Atidaro patvirtinimą, kad automatinis susiejimas būtų atliktas iš naujo visiems šios ataskaitos įrašams. Naudokite, kai PCT buvo papildyta trūkstamomis įmokomis ar mokėjimais.',
        },
        lv: {
          title: 'Atkārtoti sasaistīt visu atskaiti',
          body: 'Atver apstiprinājumu, lai automātisko sasaisti atkārtoti izpildītu visiem šīs atskaites ierakstiem. Izmantojiet to pēc tam, kad PCT ir pievienotas trūkstošās iemaksas vai maksājumi.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-row-download"]',
      title: 'Download the original Excel file',
      body: 'Opens the Excel file exactly as it was imported, from the PCT archive. The download link is time-limited, so refresh the page if it stops working.',
      section: 'Reading the report list',
      optional: true,
      i18n: {
        lt: {
          title: 'Atsisiųsti originalų Excel failą',
          body: 'Atidaro Excel failą iš PCT archyvo tokį, koks buvo importuotas. Atsisiuntimo nuoroda galioja ribotą laiką, todėl jei ji nebeveikia, atnaujinkite puslapį.',
        },
        lv: {
          title: 'Lejupielādēt sākotnējo Excel failu',
          body: 'Atver Excel failu no PCT arhīva tieši tādu, kāds tas tika importēts. Lejupielādes saite ir derīga ierobežotu laiku, tāpēc, ja tā vairs nedarbojas, atsvaidziniet lapu.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-row-delete"]',
      title: 'Delete a report',
      body: 'Opens a confirmation to delete the report. Every report imported from the same Excel file is deleted with it, not only this row.',
      section: 'Reading the report list',
      optional: true,
      i18n: {
        lt: {
          title: 'Ištrinti ataskaitą',
          body: 'Atidaro ataskaitos ištrynimo patvirtinimą. Kartu ištrinamos visos iš to paties Excel failo importuotos ataskaitos, ne tik ši eilutė.',
        },
        lv: {
          title: 'Dzēst atskaiti',
          body: 'Atver atskaites dzēšanas apstiprinājumu. Kopā ar to tiek dzēstas visas no tā paša Excel faila importētās atskaites, ne tikai šī rinda.',
        },
      },
    },

    // Reading the entries
    {
      selector: '[data-help="insurer-reports-entries-search"]',
      title: 'Search the entries of the selected report',
      body: 'Filters the entries of the selected report by client code, client name, policy number, payment plan policy number or policy sub-number. It is disabled until a report is selected in the upper grid.',
      section: 'Reading the entries',
      i18n: {
        lt: {
          title: 'Ieškoti pasirinktos ataskaitos įrašų',
          body: 'Filtruoja pasirinktos ataskaitos įrašus pagal kliento kodą, kliento pavadinimą, poliso numerį, mokėjimo plano poliso numerį arba poliso sub numerį. Laukelis neaktyvus, kol viršutinėje lentelėje nepasirinkta ataskaita.',
        },
        lv: {
          title: 'Meklēt izvēlētās atskaites ierakstos',
          body: 'Filtrē izvēlētās atskaites ierakstus pēc klienta koda, klienta nosaukuma, polises numura, maksājumu plāna polises numura vai polises apakšnumura. Lauks nav aktīvs, kamēr augšējā tabulā nav izvēlēta atskaite.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-entries-count"]',
      title: 'See how many entries are shown',
      body: 'Counts the entries of the selected report that are visible after the entry search and the perfect-match checkbox. It appears only while a report is selected.',
      section: 'Reading the entries',
      optional: true,
      i18n: {
        lt: {
          title: 'Matyti, kiek įrašų rodoma',
          body: 'Skaičiuoja pasirinktos ataskaitos įrašus, matomus pritaikius įrašų paiešką ir tobulų atitikmenų žymimąjį langelį. Rodomas tik tada, kai pasirinkta ataskaita.',
        },
        lv: {
          title: 'Redzēt, cik ierakstu ir parādīts',
          body: 'Skaita izvēlētās atskaites ierakstus, kas redzami pēc ierakstu meklēšanas un ideālo atbilstību izvēles rūtiņas. Tiek rādīts tikai tad, kad ir izvēlēta atskaite.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-entries-grid"]',
      title: 'Compare an entry with the PCT data',
      body: 'Each row is one policy of the report; rows the insurer split for the same policy and commission type were merged into one at import. The first columns hold the insurer figures, the "(PP)" columns the matched installment and "Premium (P)" the matched payment; a red cell means the insurer value differs from PCT (policy number, premium, premium without tax, commission rate, commission or broker premium collection). Orange rows were matched automatically, and the client name links to the client in GOS once the entry is matched.',
      section: 'Reading the entries',
      i18n: {
        lt: {
          title: 'Palyginti įrašą su PCT duomenimis',
          body: 'Kiekviena eilutė – vienas ataskaitos polisas; draudiko išskaidytos to paties poliso ir komisinių tipo eilutės importuojant sujungiamos į vieną. Pirmuose stulpeliuose – draudiko duomenys, „(mokėjimo planas)“ stulpeliuose – susieta įmoka, o „Įmokos suma (mokėjimas)“ – susietas mokėjimas; raudonas langelis reiškia, kad draudiko reikšmė skiriasi nuo PCT (poliso numeris, įmoka, įmoka be mokesčių, komisinių tarifas, komisiniai ar broker premium collection). Oranžinės eilutės susietos automatiškai, o susietame įraše kliento pavadinimas veda į klientą GOS sistemoje.',
        },
        lv: {
          title: 'Salīdzināt ierakstu ar PCT datiem',
          body: 'Katra rinda ir viena atskaites polise; apdrošinātāja sadalītās tās pašas polises un komisijas veida rindas importējot tiek apvienotas vienā. Pirmajās kolonnās ir apdrošinātāja dati, kolonnās “(maksājumu plāns)” – sasaistītā iemaksa, bet “Prēmijas summa (maksājums)” – sasaistītais maksājums; sarkana šūna nozīmē, ka apdrošinātāja vērtība atšķiras no PCT (polises numurs, prēmija, prēmija bez nodokļa, komisijas likme, komisija vai broker premium collection). Oranžās rindas sasaistītas automātiski, un sasaistītam ierakstam klienta nosaukums ved uz klientu GOS.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-perfect-matches"]',
      title: 'Hide the automatic matches',
      body: 'Untick "Include perfect matches" to hide the orange rows that were matched automatically, so only unmatched and hand-matched entries remain. The choice is remembered in this browser.',
      section: 'Reading the entries',
      i18n: {
        lt: {
          title: 'Paslėpti automatinius susiejimus',
          body: 'Nuimkite žymę „Įtraukti tobulus atitikmenis“, kad paslėptumėte automatiškai susietas oranžines eilutes – liks tik nesusieti ir ranka susieti įrašai. Pasirinkimas įsimenamas šioje naršyklėje.',
        },
        lv: {
          title: 'Paslēpt automātiskās sasaistes',
          body: 'Noņemiet atzīmi “Iekļaut ideālās atbilstības”, lai paslēptu automātiski sasaistītās oranžās rindas – paliks tikai nesasaistītie un manuāli sasaistītie ieraksti. Izvēle tiek atcerēta šajā pārlūkprogrammā.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-entry-match"]',
      title: 'Match an unmatched entry',
      body: 'The link icon appears on entries that have no installment or payment yet. It opens the "Match insurer report entry" dialog for that entry.',
      section: 'Reading the entries',
      optional: true,
      i18n: {
        lt: {
          title: 'Susieti nesusietą įrašą',
          body: 'Nuorodos piktograma rodoma įrašuose, kurie dar neturi įmokos ar mokėjimo. Ji atidaro to įrašo langą „Susieti draudiko ataskaitos įrašą“.',
        },
        lv: {
          title: 'Sasaistīt nesasaistītu ierakstu',
          body: 'Saites ikona parādās ierakstiem, kuriem vēl nav iemaksas vai maksājuma. Tā atver šī ieraksta logu “Sasaistīt apdrošinātāja atskaites ierakstu”.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-entry-view"]',
      title: 'View what the entry is matched to',
      body: 'On a matched entry the info icon loads the linked payment, or the installment when there is no payment, and shows its details in a read-only dialog. Use it to check why a cell is red.',
      section: 'Reading the entries',
      optional: true,
      i18n: {
        lt: {
          title: 'Peržiūrėti, su kuo susietas įrašas',
          body: 'Susietame įraše informacijos piktograma įkelia susietą mokėjimą, o jei mokėjimo nėra – įmoką, ir parodo jo duomenis tik skaitymui skirtame lange. Naudokite, kai norite suprasti, kodėl langelis raudonas.',
        },
        lv: {
          title: 'Skatīt, ar ko ieraksts sasaistīts',
          body: 'Sasaistītam ierakstam informācijas ikona ielādē sasaistīto maksājumu vai, ja maksājuma nav, iemaksu un parāda tā datus tikai lasāmā logā. Izmantojiet to, lai saprastu, kāpēc šūna ir sarkana.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-entry-rematch"]',
      title: 'Change the match of an entry',
      body: 'On a matched entry this icon opens the same "Match insurer report entry" dialog, so you can link the entry to a different installment or payment. The new link replaces the old one.',
      section: 'Reading the entries',
      optional: true,
      i18n: {
        lt: {
          title: 'Pakeisti įrašo susiejimą',
          body: 'Susietame įraše ši piktograma atidaro tą patį langą „Susieti draudiko ataskaitos įrašą“, kad galėtumėte susieti įrašą su kita įmoka ar mokėjimu. Naujas susiejimas pakeičia senąjį.',
        },
        lv: {
          title: 'Mainīt ieraksta sasaisti',
          body: 'Sasaistītam ierakstam šī ikona atver to pašu logu “Sasaistīt apdrošinātāja atskaites ierakstu”, lai ierakstu varētu sasaistīt ar citu iemaksu vai maksājumu. Jaunā sasaiste aizstāj veco.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-entry-unmatch"]',
      title: 'Unmatch an entry',
      body: 'Removes the link to the installment and payment immediately, without a confirmation. The counts of the report update, and a linked payment becomes free for another entry.',
      section: 'Reading the entries',
      optional: true,
      i18n: {
        lt: {
          title: 'Atsieti įrašą',
          body: 'Iš karto, be patvirtinimo, pašalina ryšį su įmoka ir mokėjimu. Ataskaitos skaičiai atsinaujina, o susietas mokėjimas tampa laisvas kitam įrašui.',
        },
        lv: {
          title: 'Atsaistīt ierakstu',
          body: 'Nekavējoties, bez apstiprinājuma, noņem saiti ar iemaksu un maksājumu. Atskaites skaitītāji atjaunojas, un sasaistītais maksājums kļūst brīvs citam ierakstam.',
        },
      },
    },

    // Import an insurer report
    {
      selector: '[data-help="insurer-reports-import-warning"]',
      title: 'Know what a re-import replaces',
      body: 'If any report number in the file already exists for the chosen insurer, every report imported from that earlier file is deleted, with its entries and matches, before the new file is saved. To correct a report, import the complete corrected file rather than a part of it.',
      section: 'Import an insurer report',
      context: 'import-dialog',
      i18n: {
        lt: {
          title: 'Žinoti, ką pakeičia pakartotinis importas',
          body: 'Jei kuris nors failo ataskaitos numeris jau egzistuoja pasirinktam draudikui, visos iš ankstesnio failo importuotos ataskaitos kartu su įrašais ir susiejimais ištrinamos prieš išsaugant naują failą. Norėdami pataisyti ataskaitą, importuokite visą pataisytą failą, o ne jo dalį.',
        },
        lv: {
          title: 'Zināt, ko aizstāj atkārtots imports',
          body: 'Ja kāds faila atskaites numurs izvēlētajam apdrošinātājam jau pastāv, visas no iepriekšējā faila importētās atskaites kopā ar ierakstiem un sasaistēm tiek dzēstas pirms jaunā faila saglabāšanas. Lai labotu atskaiti, importējiet visu laboto failu, nevis tā daļu.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-import-insurer"]',
      title: 'Pick the insurer',
      body: 'Required. Type part of the insurer name and choose it; all reports in the file are saved for this insurer, and automatic matching only looks at installments and payments of this insurer.',
      section: 'Import an insurer report',
      context: 'import-dialog',
      i18n: {
        lt: {
          title: 'Pasirinkti draudiką',
          body: 'Privaloma. Įveskite dalį draudiko pavadinimo ir pasirinkite jį; visos failo ataskaitos išsaugomos šiam draudikui, o automatinis susiejimas ieško tik šio draudiko įmokų ir mokėjimų.',
        },
        lv: {
          title: 'Izvēlēties apdrošinātāju',
          body: 'Obligāts. Ievadiet daļu apdrošinātāja nosaukuma un izvēlieties to; visas faila atskaites tiek saglabātas šim apdrošinātājam, un automātiskā sasaiste meklē tikai šī apdrošinātāja iemaksas un maksājumus.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-import-company"]',
      title: 'Pick the GrECo company',
      body: 'Required, and preselected when you work for one company only. Matching is limited to this company, and the server refuses the import for a company you have no access to.',
      section: 'Import an insurer report',
      context: 'import-dialog',
      i18n: {
        lt: {
          title: 'Pasirinkti GrECo įmonę',
          body: 'Privaloma; jei dirbate tik su viena įmone, ji parenkama iš anksto. Susiejama tik šios įmonės ribose, o įmonei, prie kurios neturite prieigos, serveris importo neleidžia.',
        },
        lv: {
          title: 'Izvēlēties GrECo uzņēmumu',
          body: 'Obligāts; ja strādājat tikai ar vienu uzņēmumu, tas ir izvēlēts iepriekš. Sasaiste notiek tikai šī uzņēmuma ietvaros, un uzņēmumam, kuram jums nav piekļuves, serveris importu noraida.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-import-currency"]',
      title: 'Pick the currency',
      body: 'Required and set to EUR by default. Change it only when the insurer reports in another currency; the currency applies to every report in the file.',
      section: 'Import an insurer report',
      context: 'import-dialog',
      i18n: {
        lt: {
          title: 'Pasirinkti valiutą',
          body: 'Privaloma; pagal nutylėjimą nustatyta EUR. Keiskite tik tada, kai draudikas ataskaitą pateikia kita valiuta; valiuta taikoma visoms failo ataskaitoms.',
        },
        lv: {
          title: 'Izvēlēties valūtu',
          body: 'Obligāts; pēc noklusējuma iestatīts EUR. Mainiet to tikai tad, ja apdrošinātājs atskaiti iesniedz citā valūtā; valūta attiecas uz visām faila atskaitēm.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-import-year"]',
      title: 'Choose the report year',
      body: 'The year the report covers, from the current year back six years. It starts as the year of the previous month.',
      section: 'Import an insurer report',
      context: 'import-dialog',
      i18n: {
        lt: {
          title: 'Pasirinkti ataskaitos metus',
          body: 'Metai, už kuriuos teikiama ataskaita, nuo einamųjų metų iki šešerių metų atgal. Pradžioje parinkti praėjusio mėnesio metai.',
        },
        lv: {
          title: 'Izvēlēties atskaites gadu',
          body: 'Gads, par kuru ir atskaite, no tekošā gada līdz sešiem gadiem atpakaļ. Sākumā izvēlēts iepriekšējā mēneša gads.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-import-month"]',
      title: 'Choose the report month',
      body: 'Defaults to the previous month. The report date saved is the last day of the chosen month; a booking date inside the Excel file is ignored.',
      section: 'Import an insurer report',
      context: 'import-dialog',
      i18n: {
        lt: {
          title: 'Pasirinkti ataskaitos mėnesį',
          body: 'Pagal nutylėjimą – praėjęs mėnuo. Išsaugoma ataskaitos data yra paskutinė pasirinkto mėnesio diena; Excel faile esanti apskaitos data ignoruojama.',
        },
        lv: {
          title: 'Izvēlēties atskaites mēnesi',
          body: 'Pēc noklusējuma – iepriekšējais mēnesis. Saglabātais atskaites datums ir izvēlētā mēneša pēdējā diena; Excel failā esošais grāmatošanas datums tiek ignorēts.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-import-file"]',
      title: 'Add the Excel file',
      body: 'Drag one file here or click "Browse Files". The first row of the first sheet must hold the columns Insurer report no, Client code, Client name, Policy number, Commission percentage, Premium amount, Commission amount, Commission type and Report net (Premium amount without tax is optional). Commission type must read "Broker premium collection" or "Insurer premium collection". Save the file as .xlsx: an old .xls file can be picked but the server cannot read it.',
      section: 'Import an insurer report',
      context: 'import-dialog',
      i18n: {
        lt: {
          title: 'Pridėti Excel failą',
          body: 'Nutempkite čia vieną failą arba spustelėkite „Ieškoti“. Pirmo lapo pirmoje eilutėje turi būti stulpeliai Insurer report no, Client code, Client name, Policy number, Commission percentage, Premium amount, Commission amount, Commission type ir Report net (Premium amount without tax – neprivalomas). Commission type reikšmė turi būti „Broker premium collection“ arba „Insurer premium collection“. Failą išsaugokite .xlsx formatu: senas .xls failas pasirenkamas, bet serveris jo neperskaito.',
        },
        lv: {
          title: 'Pievienot Excel failu',
          body: 'Ievelciet šeit vienu failu vai noklikšķiniet “Browse Files”. Pirmās lapas pirmajā rindā jābūt kolonnām Insurer report no, Client code, Client name, Policy number, Commission percentage, Premium amount, Commission amount, Commission type un Report net (Premium amount without tax nav obligāta). Commission type vērtībai jābūt “Broker premium collection” vai “Insurer premium collection”. Saglabājiet failu .xlsx formātā: vecu .xls failu var izvēlēties, bet serveris to nevar nolasīt.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-import-submit"]',
      title: 'Import the file',
      body: 'Enabled once insurer, GrECo company, currency and file are set. The server creates one report per distinct report number in the file, merges rows of the same policy and commission type, and matches the entries automatically. Any problem in the file, such as a missing column or a text where a number is expected, rejects the whole file with a message naming the column and row.',
      section: 'Import an insurer report',
      context: 'import-dialog',
      i18n: {
        lt: {
          title: 'Importuoti failą',
          body: 'Mygtukas „Importuoti“ tampa aktyvus, kai pasirinkti draudikas, GrECo įmonė, valiuta ir failas. Serveris kiekvienam skirtingam failo ataskaitos numeriui sukuria atskirą ataskaitą, sujungia to paties poliso ir komisinių tipo eilutes ir automatiškai susieja įrašus. Bet kokia failo klaida, pvz., trūkstamas stulpelis ar tekstas vietoj skaičiaus, atmeta visą failą su pranešimu, nurodančiu stulpelį ir eilutę.',
        },
        lv: {
          title: 'Importēt failu',
          body: 'Poga “Import” kļūst aktīva, kad izvēlēts apdrošinātājs, GrECo uzņēmums, valūta un fails. Serveris katram atšķirīgam faila atskaites numuram izveido atsevišķu atskaiti, apvieno tās pašas polises un komisijas veida rindas un automātiski sasaista ierakstus. Jebkura kļūda failā, piemēram, trūkstoša kolonna vai teksts skaitļa vietā, noraida visu failu ar ziņojumu, kurā norādīta kolonna un rinda.',
        },
      },
    },

    // Match an entry by hand
    {
      selector: '[data-help="insurer-reports-match-type"]',
      title: 'Choose installment or payment',
      body: '"Payment plan entry" links the entry to an installment; "Payment" links it to a payment already recorded in PCT, i.e. money cleared against an installment. Broker premium collection entries are normally matched to the payment, insurer premium collection entries to the installment.',
      section: 'Match an entry by hand',
      context: 'match-entry-dialog',
      i18n: {
        lt: {
          title: 'Pasirinkti įmoką arba mokėjimą',
          body: '„Mokėjimo plano įrašas“ susieja įrašą su įmoka; „Mokėjimas“ – su PCT jau užregistruotu mokėjimu, t. y. pinigais, priskirtais įmokai. Broker premium collection įrašai paprastai susiejami su mokėjimu, insurer premium collection įrašai – su įmoka.',
        },
        lv: {
          title: 'Izvēlēties iemaksu vai maksājumu',
          body: '“Maksājumu plāna ieraksts” sasaista ierakstu ar iemaksu; “Maksājums” – ar PCT jau reģistrētu maksājumu, t. i., naudu, kas piesaistīta iemaksai. Broker premium collection ierakstus parasti sasaista ar maksājumu, insurer premium collection ierakstus – ar iemaksu.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-match-target"]',
      title: 'Find the installment or payment',
      body: 'The search only offers installments and payments of the same insurer and GrECo company as the report. For a payment plan entry type a client name or policy number; for a payment enter the exact policy number. Each option shows client, policy number and premium, and payments also their payment date.',
      section: 'Match an entry by hand',
      context: 'match-entry-dialog',
      i18n: {
        lt: {
          title: 'Rasti įmoką arba mokėjimą',
          body: 'Paieška siūlo tik ataskaitos draudiko ir GrECo įmonės įmokas bei mokėjimus. Mokėjimo plano įrašui įveskite kliento pavadinimą arba poliso numerį; mokėjimui – tikslų poliso numerį. Kiekviename pasirinkime rodomas klientas, poliso numeris ir įmoka, o mokėjimuose – ir mokėjimo data.',
        },
        lv: {
          title: 'Atrast iemaksu vai maksājumu',
          body: 'Meklēšana piedāvā tikai atskaites apdrošinātāja un GrECo uzņēmuma iemaksas un maksājumus. Maksājumu plāna ierakstam ievadiet klienta nosaukumu vai polises numuru; maksājumam – precīzu polises numuru. Katrā izvēlē redzams klients, polises numurs un prēmija, bet maksājumiem arī maksājuma datums.',
        },
      },
    },
    {
      selector: '[data-help="insurer-reports-match-submit"]',
      title: 'Save the match',
      body: 'Links the entry and replaces any earlier link; the row is no longer orange because it now counts as a manual match. Choosing a payment also links the installment that payment belongs to. A payment that is already linked to another entry is refused.',
      section: 'Match an entry by hand',
      context: 'match-entry-dialog',
      i18n: {
        lt: {
          title: 'Išsaugoti susiejimą',
          body: '„Susieti“ susieja įrašą ir pakeičia ankstesnį susiejimą; eilutė nebėra oranžinė, nes dabar tai rankinis susiejimas. Pasirinkus mokėjimą, kartu susiejama ir įmoka, kuriai tas mokėjimas priklauso. Mokėjimas, jau susietas su kitu įrašu, atmetamas.',
        },
        lv: {
          title: 'Saglabāt sasaisti',
          body: '“Sasaistīt” sasaista ierakstu un aizstāj iepriekšējo sasaisti; rinda vairs nav oranža, jo tagad tā ir manuāla sasaiste. Izvēloties maksājumu, tiek sasaistīta arī iemaksa, kurai šis maksājums pieder. Maksājums, kas jau sasaistīts ar citu ierakstu, tiek noraidīts.',
        },
      },
    },

    // Re-match a report
    {
      selector: '[data-help="insurer-reports-rematch-confirm"]',
      title: 'Confirm the re-match',
      body: 'Every entry of the report is first unlinked and then matched again by the automatic rules, including entries you matched by hand, so manual matches can be overwritten. Re-match only after the missing installments or payments exist, and check the hand-matched entries afterwards.',
      section: 'Re-match a report',
      context: 'rematch-dialog',
      i18n: {
        lt: {
          title: 'Patvirtinti pakartotinį susiejimą',
          body: '„Susieti iš naujo“ pirmiausia atsieja visus ataskaitos įrašus, o tada vėl juos susieja pagal automatines taisykles – įskaitant ranka susietus įrašus, todėl rankiniai susiejimai gali būti perrašyti. Susiekite iš naujo tik tada, kai trūkstamos įmokos ar mokėjimai jau yra, ir po to patikrinkite ranka susietus įrašus.',
        },
        lv: {
          title: 'Apstiprināt atkārtotu sasaisti',
          body: '“Sasaistīt atkārtoti” vispirms atsaista visus atskaites ierakstus un pēc tam tos atkal sasaista pēc automātiskajiem noteikumiem – arī manuāli sasaistītos, tāpēc manuālās sasaistes var tikt pārrakstītas. Sasaistiet atkārtoti tikai tad, kad trūkstošās iemaksas vai maksājumi jau ir pieejami, un pēc tam pārbaudiet manuāli sasaistītos ierakstus.',
        },
      },
    },

    // Delete a report
    {
      selector: '[data-help="insurer-reports-delete-confirm"]',
      title: 'Confirm the deletion',
      body: 'Deletes every report that came from the same Excel file, together with their entries, and frees the installments and payments they were linked to. There is no undo on this page; import the file again if you deleted it by mistake.',
      section: 'Delete a report',
      context: 'delete-dialog',
      i18n: {
        lt: {
          title: 'Patvirtinti ištrynimą',
          body: '„Ištrinti“ pašalina visas iš to paties Excel failo gautas ataskaitas kartu su jų įrašais ir atlaisvina susietas įmokas bei mokėjimus. Šiame puslapyje veiksmo atšaukti negalima; jei ištrynėte per klaidą, importuokite failą iš naujo.',
        },
        lv: {
          title: 'Apstiprināt dzēšanu',
          body: '“Delete” dzēš visas no tā paša Excel faila iegūtās atskaites kopā ar to ierakstiem un atbrīvo sasaistītās iemaksas un maksājumus. Šajā lapā darbību atsaukt nevar; ja izdzēsāt kļūdas pēc, importējiet failu vēlreiz.',
        },
      },
    },

    // View the matched payment or installment
    {
      selector: '[data-help="insurer-reports-info-dialog"]',
      title: 'Read the matched payment or installment',
      body: 'Shows client, policy, insurer, currency, premium, commission and booking date of the linked record. An installment adds its premium period, total, paid amount and remaining debt; a payment adds its payment date, the date it was declared to the insurer and its comment. The dialog is read-only.',
      section: 'View the matched payment or installment',
      context: 'info-dialog',
      i18n: {
        lt: {
          title: 'Peržiūrėti susietą mokėjimą ar įmoką',
          body: 'Rodomas susieto įrašo klientas, polisas, draudikas, valiuta, įmoka, komisiniai ir apskaitos data. Įmokai papildomai rodomas įmokos laikotarpis, visa suma, sumokėta suma ir likusi skola; mokėjimui – mokėjimo data, deklaravimo draudikui data ir komentaras. Langas skirtas tik peržiūrai.',
        },
        lv: {
          title: 'Apskatīt sasaistīto maksājumu vai iemaksu',
          body: 'Tiek rādīts sasaistītā ieraksta klients, polise, apdrošinātājs, valūta, prēmija, komisija un grāmatošanas datums. Iemaksai papildus redzams prēmijas periods, kopsumma, samaksātā summa un atlikušais parāds; maksājumam – maksājuma datums, deklarēšanas apdrošinātājam datums un komentārs. Logs ir tikai apskatei.',
        },
      },
    },
  ],
  i18n: {
    lt: {
      title: 'Draudiko ataskaitos',
      intro:
        'Importuokite Excel ataskaitas, kuriose draudikai nurodo įmokas ir komisinius pagal polisus, ir patikrinkite kiekvieną įrašą su PCT turima įmoka (mokėjimo plano įrašu) arba mokėjimu. Importuojant įrašai susiejami automatiškai; šiame puslapyje likę įrašai susiejami ranka ir peržiūrimi skirtumai.',
      tips: [
        'Draudiko ataskaita – tai draudiko pusė įmokų surinkime: kiekvienas įrašas yra polisas, už kurį draudikas nurodo įmoką ir komisinius. Susiejimas sieja įrašą su PCT įmoka ar mokėjimu, todėl skirtumai matomi kaip raudoni langeliai ir nesutapusių įrašų skaičiai.',
        'Automatinis susiejimas vertina tik to paties draudiko ir GrECo įmonės mokėjimo plano įrašus, kurių poliso numeris arba poliso sub numeris sutampa su ataskaitos poliso numeriu; iš kelių kandidatų parenkamas tas, kurio įmoka artimiausia. Broker premium collection įrašams pirmenybė teikiama PCT užregistruotam to poliso mokėjimui, o vienas mokėjimas gali būti susietas tik su vienu įrašu.',
        'Spalvos: oranžinės įrašų eilutės susietos automatiškai, raudoni langeliai skiriasi nuo PCT duomenų, o raudonas „Nesutapę įrašai“ skaičius reiškia, kad ataskaitoje dar yra nesusietų įrašų.',
        'Susieto įrašo draudiko ataskaitos numeris, data, įmoka, komisiniai ir mokėjimo data rodomi ir prie įmokos puslapyje „Mokėjimų planas“.',
        'Importuoti, susieti, susieti iš naujo ir trinti galima tik turint rašymo teises PCT; tik skaitymo teises turintys naudotojai gali peržiūrėti ataskaitas, bet serveris šių pakeitimų neleidžia.',
      ],
      sections: {
        'Getting around': 'Naršymas',
        'Finding a report': 'Ataskaitos paieška',
        'Reading the report list': 'Ataskaitų sąrašo skaitymas',
        'Reading the entries': 'Įrašų skaitymas',
        'Import an insurer report': 'Draudiko ataskaitos importas',
        'Match an entry by hand': 'Įrašo susiejimas ranka',
        'Re-match a report': 'Ataskaitos susiejimas iš naujo',
        'Delete a report': 'Ataskaitos ištrynimas',
        'View the matched payment or installment': 'Susieto mokėjimo ar įmokos peržiūra',
      },
      contexts: {
        'add-menu': 'Antroje įrankių juostos eilutėje spustelėkite „Pridėti draudiko ataskaitą“.',
        'import-dialog': 'Spustelėkite „Pridėti draudiko ataskaitą“, tada „Importuoti draudiko ataskaitą“.',
        'match-entry-dialog':
          'Pasirinkite ataskaitą, tada paskutiniame įrašo stulpelyje spustelėkite nuorodos (arba pakartotinio susiejimo) piktogramą.',
        'rematch-dialog': 'Paskutiniame ataskaitos eilutės stulpelyje spustelėkite pakartotinio susiejimo piktogramą.',
        'delete-dialog': 'Paskutiniame ataskaitos eilutės stulpelyje spustelėkite ištrynimo piktogramą.',
        'info-dialog':
          'Pasirinkite ataskaitą, tada paskutiniame susieto įrašo stulpelyje spustelėkite informacijos piktogramą.',
      },
    },
    lv: {
      title: 'Apdrošinātāja atskaites',
      intro:
        'Importējiet Excel atskaites, kurās apdrošinātāji norāda prēmiju un komisiju pa polisēm, un salīdziniet katru ierakstu ar PCT esošo iemaksu (maksājumu plāna ierakstu) vai maksājumu. Importējot ieraksti tiek sasaistīti automātiski; šajā lapā atlikušos ierakstus sasaista manuāli un pārskata atšķirības.',
      tips: [
        'Apdrošinātāja atskaite ir apdrošinātāja puse prēmiju iekasēšanā: katrs ieraksts ir polise, par kuru apdrošinātājs norāda prēmiju un komisiju. Sasaiste savieno ierakstu ar PCT iemaksu vai maksājumu, tāpēc atšķirības redzamas kā sarkanas šūnas un nesaskaņoto ierakstu skaits.',
        'Automātiskā sasaiste ņem vērā tikai tā paša apdrošinātāja un GrECo uzņēmuma maksājumu plāna ierakstus, kuru polises numurs vai polises apakšnumurs sakrīt ar atskaites polises numuru; no vairākiem kandidātiem tiek izvēlēts tas, kura prēmija ir vistuvākā. Broker premium collection ierakstiem priekšroka ir šīs polises PCT reģistrētam maksājumam, un vienu maksājumu var sasaistīt tikai ar vienu ierakstu.',
        'Krāsas: oranžās ierakstu rindas sasaistītas automātiski, sarkanās šūnas atšķiras no PCT datiem, un sarkans “Nesaskaņotie ieraksti” skaits nozīmē, ka atskaitē vēl ir nesasaistīti ieraksti.',
        'Sasaistīta ieraksta apdrošinātāja atskaites numurs, datums, prēmija, komisija un maksājuma datums ir redzami arī pie iemaksas lapā “Payment plans”.',
        'Importēt, sasaistīt, atkārtoti sasaistīt un dzēst var tikai ar rakstīšanas tiesībām PCT; lietotāji ar tikai lasīšanas tiesībām var skatīt atskaites, bet serveris šīs izmaiņas noraida.',
      ],
      sections: {
        'Getting around': 'Orientēšanās',
        'Finding a report': 'Atskaites meklēšana',
        'Reading the report list': 'Atskaišu saraksta lasīšana',
        'Reading the entries': 'Ierakstu lasīšana',
        'Import an insurer report': 'Apdrošinātāja atskaites imports',
        'Match an entry by hand': 'Ieraksta manuāla sasaiste',
        'Re-match a report': 'Atskaites atkārtota sasaiste',
        'Delete a report': 'Atskaites dzēšana',
        'View the matched payment or installment': 'Sasaistītā maksājuma vai iemaksas apskate',
      },
      contexts: {
        'add-menu': 'Rīkjoslas otrajā rindā noklikšķiniet “Pievienot apdrošinātāja atskaiti”.',
        'import-dialog': 'Noklikšķiniet “Pievienot apdrošinātāja atskaiti”, pēc tam “Importēt apdrošinātāja atskaiti”.',
        'match-entry-dialog':
          'Izvēlieties atskaiti, pēc tam ieraksta pēdējā kolonnā noklikšķiniet saites (vai atkārtotas sasaistes) ikonu.',
        'rematch-dialog': 'Atskaites rindas pēdējā kolonnā noklikšķiniet atkārtotas sasaistes ikonu.',
        'delete-dialog': 'Atskaites rindas pēdējā kolonnā noklikšķiniet dzēšanas ikonu.',
        'info-dialog':
          'Izvēlieties atskaiti, pēc tam sasaistītā ieraksta pēdējā kolonnā noklikšķiniet informācijas ikonu.',
      },
    },
  },
};
